import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { Brain, Check, Plus, Trash2, X } from 'lucide-react'
import type { Memory, MemoryKind, MemoryScope } from '@shared/types'
import { api } from '../../lib/api'
import { useStore } from '../../store'

const KINDS: { value: MemoryKind; label: string }[] = [
  { value: 'watchlist', label: 'Watchlist' },
  { value: 'portfolio', label: 'Portfolio' },
  { value: 'preference', label: 'Preference' },
  { value: 'fact', label: 'Fact' },
]

function valueToText(v: unknown): string {
  if (v == null) return ''
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v)
  return JSON.stringify(v)
}
function textToValue(t: string): unknown {
  const s = t.trim()
  if (!s) return ''
  if (/^[[{]/.test(s)) {
    try {
      return JSON.parse(s)
    } catch {
      /* keep string */
    }
  }
  if (/^-?\d+(\.\d+)?$/.test(s)) return Number(s)
  return s
}

const inputCls =
  'min-w-0 flex-1 rounded-lg border border-[var(--line-soft)] bg-[var(--color-inset)] px-2 py-1.5 text-[12px] text-fg placeholder:text-fg-faint focus:outline-none'

const SCOPE_LABEL: Record<MemoryScope, string> = { global: 'Account', project: 'Project', chat: 'This chat' }

function Row({
  mem,
  onSave,
  onDelete,
  onMove,
  scopes,
}: {
  mem: Memory
  onSave: (mem: Memory, label: string, value: unknown) => void
  onDelete: (mem: Memory) => void
  onMove: (mem: Memory, target: MemoryScope) => void
  scopes: MemoryScope[]
}) {
  const [label, setLabel] = useState(mem.label)
  const [value, setValue] = useState(valueToText(mem.value))
  const dirty = label !== mem.label || value !== valueToText(mem.value)
  return (
    <div className="rounded-lg border border-[var(--line-soft)] bg-[var(--color-surface-2)] p-2">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1 space-y-1">
          <input value={label} onChange={(e) => setLabel(e.target.value)} className="w-full bg-transparent text-[12.5px] font-medium text-fg focus:outline-none" />
          <textarea value={value} onChange={(e) => setValue(e.target.value)} rows={1} className="num w-full resize-none bg-transparent text-[12px] text-fg-muted focus:outline-none" />
        </div>
        <div className="flex shrink-0 flex-col gap-1">
          {dirty && (
            <button onClick={() => onSave(mem, label.trim() || mem.label, textToValue(value))} title="Save" className="rounded p-1 text-up hover:bg-[var(--color-surface-3)]">
              <Check size={14} />
            </button>
          )}
          <button onClick={() => onDelete(mem)} title="Delete" className="rounded p-1 text-fg-subtle hover:text-down">
            <Trash2 size={14} />
          </button>
        </div>
      </div>
      {scopes.length > 1 && (
        <div className="mt-1.5 flex items-center gap-1.5 border-t border-[var(--line-soft)] pt-1.5">
          <span className="text-[10px] uppercase tracking-wide text-fg-faint">Tier</span>
          <select
            value={mem.scope}
            onChange={(e) => onMove(mem, e.target.value as MemoryScope)}
            title="Move to a different memory tier"
            className="rounded border border-[var(--line-soft)] bg-[var(--color-inset)] px-1.5 py-0.5 text-[11px] text-fg-muted focus:outline-none"
          >
            {scopes.map((s) => (
              <option key={s} value={s}>{SCOPE_LABEL[s]}</option>
            ))}
          </select>
        </div>
      )}
    </div>
  )
}

function AddRow({ onAdd }: { onAdd: (kind: MemoryKind, label: string, value: unknown) => void }) {
  const [kind, setKind] = useState<MemoryKind>('fact')
  const [label, setLabel] = useState('')
  const [value, setValue] = useState('')
  const add = () => {
    if (!label.trim()) return
    onAdd(kind, label.trim(), textToValue(value))
    setLabel('')
    setValue('')
  }
  return (
    <div className="flex items-center gap-1.5">
      <select value={kind} onChange={(e) => setKind(e.target.value as MemoryKind)} className="rounded-lg border border-[var(--line-soft)] bg-[var(--color-inset)] px-2 py-1.5 text-[12px] text-fg focus:outline-none">
        {KINDS.map((k) => (
          <option key={k.value} value={k.value}>{k.label}</option>
        ))}
      </select>
      <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="label" className={inputCls} />
      <input value={value} onChange={(e) => setValue(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} placeholder="value" className={inputCls} />
      <button onClick={add} title="Add" className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[var(--color-accent)] text-[var(--color-base)]">
        <Plus size={16} />
      </button>
    </div>
  )
}

function Tier({
  title,
  subtitle,
  items,
  handlers,
  scopes,
  onMove,
}: {
  title: string
  subtitle: string
  items: Memory[] | null
  scopes: MemoryScope[]
  onMove: (mem: Memory, target: MemoryScope) => void
  handlers: {
    add: (kind: MemoryKind, label: string, value: unknown) => void
    save: (mem: Memory, label: string, value: unknown) => void
    del: (mem: Memory) => void
  }
}) {
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between">
        <span className="text-[12px] font-semibold uppercase tracking-[0.1em] text-fg-muted">{title}</span>
        <span className="text-[10.5px] text-fg-faint">{subtitle}</span>
      </div>
      <div className="mb-2"><AddRow onAdd={handlers.add} /></div>
      {items === null ? (
        <div className="py-3 text-center text-[12px] text-fg-faint">Loading…</div>
      ) : items.length === 0 ? (
        <div className="py-3 text-center text-[12px] text-fg-faint">Nothing here yet.</div>
      ) : (
        <div className="space-y-1.5">
          {items.map((m) => (
            <Row key={m.id} mem={m} onSave={handlers.save} onDelete={handlers.del} onMove={onMove} scopes={scopes} />
          ))}
        </div>
      )}
    </div>
  )
}

export function MemoryPanel() {
  const open = useStore((s) => s.memoryOpen)
  const toggle = useStore((s) => s.toggleMemory)
  const globalMem = useStore((s) => s.memory)
  const upsert = useStore((s) => s.upsertMemory)
  const removeGlobal = useStore((s) => s.removeMemory)
  const activeChatId = useStore((s) => s.activeChatId)
  const chats = useStore((s) => s.chats)
  const projects = useStore((s) => s.projects)

  const activeChat = chats.find((c) => c.id === activeChatId)
  const projectId = activeChat?.projectId ?? null
  const projectName = projects.find((p) => p.id === projectId)?.name

  const [projMem, setProjMem] = useState<Memory[] | null>(null)
  const [chatMem, setChatMem] = useState<Memory[] | null>(null)

  const reloadProj = () => {
    if (projectId) api.listMemory('project', projectId).then(setProjMem).catch(() => setProjMem([]))
    else setProjMem(null)
  }
  const reloadChat = () => {
    if (activeChatId) api.listMemory('chat', activeChatId).then(setChatMem).catch(() => setChatMem([]))
    else setChatMem(null)
  }

  useEffect(() => {
    if (!open) return
    reloadProj()
    reloadChat()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId, activeChatId])

  const tier = (scope: MemoryScope, scopeId: string | null, reload: () => void) => ({
    add: (kind: MemoryKind, label: string, value: unknown) => {
      void upsert(scope, scopeId, kind, label, value).then(reload)
    },
    save: async (mem: Memory, label: string, value: unknown) => {
      if (label !== mem.label) {
        if (scope === 'global') await removeGlobal(mem.id)
        else await api.deleteMemory(mem.id).catch(() => {})
      }
      await upsert(scope, scopeId, mem.kind, label, value)
      reload()
    },
    del: (mem: Memory) => {
      if (scope === 'global') void removeGlobal(mem.id)
      else void api.deleteMemory(mem.id).then(reload).catch(() => {})
    },
  })

  const availableScopes: MemoryScope[] = ['global']
  if (projectId) availableScopes.push('project')
  if (activeChatId) availableScopes.push('chat')

  const move = async (mem: Memory, target: MemoryScope) => {
    if (target === mem.scope) return
    if (mem.scope === 'global') await removeGlobal(mem.id)
    else await api.deleteMemory(mem.id).catch(() => {})
    const targetId = target === 'project' ? projectId : target === 'chat' ? activeChatId : null
    await upsert(target, targetId, mem.kind, mem.label, mem.value)
    reloadProj()
    reloadChat()
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <div className="absolute inset-0 bg-black/55" onClick={() => toggle(false)} />
          <motion.div
            initial={{ y: 16, scale: 0.98, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 16, scale: 0.98, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 320, damping: 30 }}
            className="glass relative flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden"
            style={{ borderRadius: 'var(--radius-lg)' }}
          >
            <header className="flex items-center justify-between border-b border-[var(--line-soft)] px-4 py-3">
              <div className="flex items-center gap-2">
                <Brain size={16} className="text-fg-muted" />
                <span className="text-[14px] font-semibold text-fg">Memory</span>
              </div>
              <button onClick={() => toggle(false)} className="rounded-md p-1 text-fg-subtle hover:text-fg" title="Close">
                <X size={16} />
              </button>
            </header>

            <div className="flex-1 space-y-5 overflow-y-auto px-4 py-4">
              <Tier
                title="Account"
                subtitle="every chat"
                items={globalMem}
                scopes={availableScopes}
                onMove={move}
                handlers={tier('global', null, () => {})}
              />
              {projectId && (
                <Tier
                  title="Project"
                  subtitle={projectName ?? 'project'}
                  items={projMem}
                  scopes={availableScopes}
                  onMove={move}
                  handlers={tier('project', projectId, reloadProj)}
                />
              )}
              {activeChatId && (
                <Tier
                  title="This chat"
                  subtitle={activeChat?.title ?? 'chat'}
                  items={chatMem}
                  scopes={availableScopes}
                  onMove={move}
                  handlers={tier('chat', activeChatId, reloadChat)}
                />
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
